// Implements: F-UIX-020, F-UIX-024, F-UIX-014, F-UIX-015
/**
 * `/design/profiles` — Diseño (UX-09): the design profiles of the person, the editor with its live
 * preview and contrast, and where each profile is active.
 *
 * The page only composes: the listing, the selection and the save live in the
 * `design` containers (F-UIX-009). It sits behind `RequireSession` like every
 * route but `/login`; which profiles open the editor is the list's decision,
 * and every save is still the server's.
 *
 * Owner report, round 3 ("No comprendo cómo utilizarlo"): the page says what profiles are for and
 * how to use them in three steps. Round 4 ("sigue muy diferente al mockup"): that guide is not a
 * block above the screen but a small "¿Cómo funciona?" link on the subtitle's line, opening a
 * popover. Nothing is remembered: it starts closed every time.
 */

import { useRef, useState } from "react";
import type { ReactElement } from "react";
import { useSearchParams } from "react-router";

import { ProfileWorkspaceContainer } from "../../modules/design/containers/ProfileWorkspaceContainer";
import { useTranslation } from "../../shared/i18n";
import { Icon } from "../../shared/ui/atoms/Icon";
import { PageHeader } from "../../shared/ui/molecules/PageHeader";
import { Popover } from "../../shared/ui/molecules/Popover";
import styles from "./DesignProfilesPage.module.css";

function HowItWorks(): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={styles.howLink}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <Icon name="info" size="sm" />
        {t("design.guide.open")}
      </button>
      <Popover
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        anchorRef={anchor}
        labelledBy="design-guide-title"
        width="sm"
      >
        <div className={styles.guide}>
          <h2 id="design-guide-title" className={styles.guideTitle}>
            {t("design.guide.title")}
          </h2>
          <p className={styles.guideBody}>{t("design.guide.body")}</p>
          <ol className={styles.steps}>
            <li>{t("design.guide.first")}</li>
            <li>{t("design.guide.second")}</li>
            <li>{t("design.guide.third")}</li>
          </ol>
        </div>
      </Popover>
    </>
  );
}

export function DesignProfilesPage(): ReactElement {
  const { t } = useTranslation();
  // `?profile=<id>` is where the applied-profile indicator links (F-UIX-024).
  const [params, setParams] = useSearchParams();
  return (
    <section>
      <PageHeader title={t("design.page.title")} subtitle={t("design.page.subtitle")} aside={<HowItWorks />} />
      <ProfileWorkspaceContainer
        requestedProfileId={params.get("profile") ?? undefined}
        onOpened={(profileId) => {
          // A new history entry per profile opened, so back returns to the previous one.
          setParams(profileId === null ? {} : { profile: profileId });
        }}
      />
    </section>
  );
}
